<script setup lang="ts">
/**
 * App-local wrapper around @kori/ui ImpersonationBanner (S-SUP-050).
 *
 * Owns two pieces of app-specific plumbing the shared component intentionally
 * doesn't:
 *   - Pulls tenant / expiry state from `impersonationStore`
 *   - On "End session", calls the store mutation and routes back to the
 *     tenant detail page per spec
 */
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { ImpersonationBanner as BaseBanner, useToast } from '@kori/ui'
import { useImpersonationStore } from '@/stores/impersonation'

const impersonation = useImpersonationStore()
const router = useRouter()
const toast = useToast()

const tenantLabel = computed(() => {
  const s = impersonation.session
  if (!s) return ''
  return s.impersonatedUserName
    ? `${s.impersonatedUserName} at ${s.tenantName}`
    : s.tenantName
})

const startedAtLabel = computed(() => {
  const s = impersonation.session
  if (!s) return undefined
  return new Date(s.startedAt).toLocaleString()
})

const countdown = computed<string>(() => {
  const secs = impersonation.secondsRemaining
  if (secs <= 0) return 'expiring now'
  const m = Math.floor(secs / 60)
  const s = secs % 60
  return `${m}:${String(s).padStart(2, '0')} remaining`
})

async function onEnd(): Promise<void> {
  try {
    await impersonation.end()
    toast.success('Impersonation session ended.')
    // Per S-SUP-050: after ending, the Super Admin lands on the platform
    // dashboard (not the tenant detail they were impersonating).
    await router.push({ name: 'platform.dashboard' })
  } catch {
    toast.danger('Could not cleanly end the session — please refresh.')
  }
}
</script>

<template>
  <BaseBanner
    v-if="impersonation.isActive"
    :tenant-name="`${tenantLabel} · ${countdown}`"
    :session-started-at="startedAtLabel"
    @end-session="onEnd"
  />
</template>
